import React, { useMemo } from 'react';
import OrgCanvas, { layoutNodes, connectorPath, NODE_W, NODE_H, PADDING } from './OrgCanvas';

/* ───────────── Account Map canvas ─────────────
 *
 * Thin account-specific adapter over the shared OrgCanvas. It maps the account
 * hierarchy payload (accounts + ad-hoc placeholders) into generic org nodes and
 * delegates all rendering/interaction to OrgCanvas. Placeholders can't be a
 * parent (canBeParent: false). The account map's PNG export still imports
 * layoutHierarchy/connectorPath/NODE_W/NODE_H from here.
 */
export { connectorPath, NODE_W, NODE_H, PADDING };

// Build generic org nodes from the account hierarchy payload.
export function buildNodes(hierarchy) {
  if (!hierarchy) return { nodes: [], byKey: new Map() };
  const nodes = [];
  (hierarchy.accounts || []).forEach(a => {
    nodes.push({
      key: `a${a.id}`,
      type: 'account',
      id: a.id,
      name: a.name,
      sublabel: a.stage_name || a.industry || null,
      parentKey: a.parent_account_id != null ? `a${a.parent_account_id}` : null,
      savedX: a.hierarchy_x,
      savedY: a.hierarchy_y,
      isCurrent: a.id === hierarchy.current_account_id,
      isRoot: a.id === hierarchy.root_account_id,
      raw: a,
    });
  });
  (hierarchy.placeholders || []).forEach(p => {
    nodes.push({
      key: `p${p.id}`,
      type: 'placeholder',
      id: p.id,
      name: p.name,
      sublabel: 'Prospective subsidiary',
      parentKey: `a${p.parent_account_id}`,
      savedX: p.x,
      savedY: p.y,
      canBeParent: false, // placeholders aren't real accounts → can't be a parent
      raw: p,
    });
  });
  const byKey = new Map(nodes.map(n => [n.key, n]));
  return { nodes, byKey };
}

// Pure layout for the account hierarchy (used by the PNG export).
export function layoutHierarchy(hierarchy) {
  return layoutNodes(buildNodes(hierarchy).nodes);
}

function AccountMapCanvas({ hierarchy, canEdit, onMoveNode, onNodeClick, onConnect }) {
  const { nodes } = useMemo(() => buildNodes(hierarchy), [hierarchy]);
  return (
    <OrgCanvas
      nodes={nodes}
      canEdit={canEdit}
      onMoveNode={onMoveNode}
      onNodeClick={onNodeClick}
      onConnect={onConnect}
      connectTitle="Add a child: draw to another account to make it a subsidiary of this one"
      emptyMessage="No accounts in this hierarchy yet."
    />
  );
}

export default AccountMapCanvas;
